import React, { useContext, useEffect, useState } from "react";
import classNames from "classnames";
import pick from "lodash/pick";
import { useTranslation } from "react-i18next";
import { FollowerVariationPreviewsContext } from "../../contexts";

import useEbsApiClient from "../../hooks/useEbsApiClient.js";
import useTwitch from "../../hooks/useTwitch.js";
import Button from "../Button/Button.js";
import DebugJSON from "../DebugJSON/DebugJSON.js";
import EmbellishedHeading from "../EmbellishedHeading/EmbellishedHeading.js";
import loader from "../../lib/follower-loader.js";

import "./FollowerBuilder.scss";
import FollowerRenderer from "../FollowerRenderer/FollowerRenderer.js";
import Stack from "../Stack/Stack.js";
import bitsLogo from "../../icons/bits.svg";
import SubtleText from "../SubtleText/SubtleText.js";

const MODES = {
  BUILD_YOUR_OWN: "BUILD_YOUR_OWN",
  PREMIUM_OUTFITS: "PREMIUM_OUTFITS",
};

const STEPS = {
  INTRO: "INTRO",
  SKIN_SELECTION: "SKIN_SELECTION",
  COLOR_SELECTION: "COLOR_SELECTION",
  VARIATION_SELECTION: "VARIATION_SELECTION",
};

const getAllVariationsForSkinName = (skinName) => {
  const allSkinNamesInSpineData = loader.resources.follower.spineData.skins.map(
    (skin) => skin.name
  );

  return allSkinNamesInSpineData.filter(
    (skin) => skin === skinName || skin.startsWith(skinName)
  );
};

const filterVariationsByPremiumStatus = ({
  variations,
  premiumSkinNames,
  premium,
}) => {
  if (premium) {
    return pick(variations, premiumSkinNames);
  }

  const nonPremiumSkinNames = Object.keys(variations).filter(
    (skinName) => !premiumSkinNames.includes(skinName)
  );
  return pick(variations, nonPremiumSkinNames);
};

const BitsIcon = () => (
  <svg width="16" height="16" version="1.1" viewBox="0 0 20 20" x="0px" y="0px">
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M3 12l7-10 7 10-7 6-7-6zm2.678-.338L10 5.487l4.322 6.173-.85.728L10 11l-3.473 1.39-.849-.729z"
      fill="#222"
    ></path>
  </svg>
);

const GenericVariationOption = ({
  className,
  style,
  children,
  isPremium,
  premiumBitsCost,
  isSelected,
  isLocked,
  onClick,
}) => {
  return (
    <button
      className={classNames(
        "variation-option",
        {
          "variation-option--selected": isSelected,
          "variation-option--locked": isLocked,
        },
        className
      )}
      onClick={onClick}
      disabled={isLocked}
      style={style}
    >
      {children}
      {premiumBitsCost && isPremium && (
        <div className="variation-option__premium-indicator">
          <BitsIcon /> <span>{premiumBitsCost}</span>
        </div>
      )}
    </button>
  );
};

const FormOption = ({
  skinName,
  color,
  previews,
  isPremium,
  premiumBitsCost,
  isSelected,
  ...props
}) => {
  const previewImage = previews[`${skinName}-${color.colorOptionIndex}`];
  const classes = classNames("variation-option--form", {
    "variation-option--premium-form": isPremium,
  });
  const variationPreviewUrl = `assets/followers/variation-previews/${skinName}-${color.colorOptionIndex}.png`;

  return (
    <GenericVariationOption
      className={classes}
      isSelected={isSelected}
      isPremium={isPremium}
      premiumBitsCost={premiumBitsCost}
      {...props}
    >
      {/*<img src={variationPreviewUrl} alt={skinName} title={skinName} />*/}
      <div
        title={skinName}
        className="variation-option__preview"
        style={{ backgroundImage: `url('${variationPreviewUrl}')` }}
      />
    </GenericVariationOption>
  );
};

const FollowerBuilderInner = ({ selectedVariation, children }) => {
  return (
    <div className="follower-builder-inner">
      <div className="follower-builder-inner__preview"></div>
      <div className="follower-builder-inner__customisations">{children}</div>
    </div>
  );
};

const FollowerBuilder = ({ premiumBitsCost, onFinishBuilding = () => {} }) => {
  console.log({ premiumBitsCost });
  const { t } = useTranslation();
  const ebsApiClient = useEbsApiClient();
  const [variationData, setVariationData] = useState({
    variations: null,
    previews: null,
  });
  const [selectedVariation, setSelectedVariation] = useState({
    skinName: null,
    variationSkinName: null,
    color: null,
  });
  const [activeStep, setActiveStep] = useState(STEPS.INTRO);

  const skinNameVariations = getAllVariationsForSkinName(
    selectedVariation.skinName
  );
  const doesSelectedSkinHaveMultipleVariations = skinNameVariations.length > 1;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await ebsApiClient.get("/followers/variations");
        const basicVariations = filterVariationsByPremiumStatus({
          variations: data.variations,
          premiumSkinNames: data.premiumSkinNames,
          premium: false,
        });
        const firstVariationSkinName = Object.keys(basicVariations)[0];
        setSelectedVariation({
          skinName: firstVariationSkinName,
          variationSkinName: firstVariationSkinName,
          color: basicVariations[firstVariationSkinName][0],
        });
        setVariationData(data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    if (activeStep === STEPS.SKIN_SELECTION) {
      const firstColor =
        variationData.variations[selectedVariation.skinName][0];
      if (
        selectedVariation.color.colorOptionIndex !== firstColor.colorOptionIndex
      ) {
        setSelectedVariation((variation) => ({
          ...variation,
          color: firstColor,
        }));
      }
    }
  }, [activeStep, selectedVariation, variationData]);

  useEffect(() => {
    (async () => {
      await ebsApiClient.patch("/followers/me", {
        current_step: activeStep,
      });
    })();
  }, [activeStep]);

  //useEffect(() => {
  //  setTimeout(() => {
  //    setActiveStep(STEPS.SKIN_SELECTION);
  //  }, 200);
  //}, []);

  if (!variationData.variations) {
    return <p>Loading...</p>;
  }

  const basicSkins = filterVariationsByPremiumStatus({
    variations: variationData.variations,
    premiumSkinNames: variationData.premiumSkinNames,
    premium: false,
  });

  const premiumSkins = filterVariationsByPremiumStatus({
    variations: variationData.variations,
    premiumSkinNames: variationData.premiumSkinNames,
    premium: true,
  });

  const handleChangeSkin = (skinName, color) => {
    setSelectedVariation({
      skinName,
      variationSkinName: skinName,
      color,
    });
  };

  const handleChangeOption = (property, value) => {
    setSelectedVariation((options) => ({
      ...options,
      [property]: value,
    }));
  };

  const handleClickFinish = () => {
    const premium = Boolean(premiumSkins[selectedVariation.skinName]);
    onFinishBuilding({
      ...selectedVariation,
      skinName: selectedVariation.variationSkinName,
      premium,
    });
  };

  const contentByStep = {
    [STEPS.INTRO]: (
      <p>{t("features.followers.followerBuilder.steps.intro.message")}</p>
    ),
    [STEPS.SKIN_SELECTION]: (
      <div className="builder__form-grid" key="skin-selection-grid">
        {Object.entries(basicSkins).map(([skinName, colors]) => (
          <FormOption
            premiumBitsCost={premiumBitsCost}
            key={skinName}
            skinName={skinName}
            color={colors[0]}
            previews={variationData.previews}
            isSelected={selectedVariation.skinName === skinName}
            onClick={() => handleChangeSkin(skinName, colors[0])}
          />
        ))}
        {Object.entries(premiumSkins).map(([skinName, colors]) => (
          <FormOption
            premiumBitsCost={premiumBitsCost}
            key={skinName}
            skinName={skinName}
            isPremium
            color={colors[0]}
            previews={variationData.previews}
            isSelected={selectedVariation.skinName === skinName}
            onClick={() => handleChangeSkin(skinName, colors[0])}
          />
        ))}
      </div>
    ),
    [STEPS.COLOR_SELECTION]: (
      <div className="builder__form-grid" key="color-selection-grid">
        {variationData.variations[selectedVariation.skinName].map((color) => (
          <FormOption
            premiumBitsCost={premiumBitsCost}
            key={color.colorOptionIndex}
            skinName={selectedVariation.skinName}
            color={color}
            previews={variationData.previews}
            isSelected={
              selectedVariation.color
                ? selectedVariation.color.colorOptionIndex ===
                  color.colorOptionIndex
                : false
            }
            onClick={() => handleChangeOption("color", color)}
          />
        ))}
      </div>
    ),
    [STEPS.VARIATION_SELECTION]: (
      <div className="builder__form-grid" key="variation-selection-grid">
        {skinNameVariations.map((variationSkinName) => (
          <FormOption
            premiumBitsCost={premiumBitsCost}
            key={variationSkinName}
            skinName={variationSkinName}
            color={selectedVariation.color}
            previews={variationData.previews}
            isSelected={
              selectedVariation.variationSkinName === variationSkinName
            }
            onClick={() =>
              handleChangeOption("variationSkinName", variationSkinName)
            }
          />
        ))}
      </div>
    ),
  };

  const footerByStep = {
    [STEPS.INTRO]: (
      <Button onClick={() => setActiveStep(STEPS.SKIN_SELECTION)}>
        {t(
          "features.followers.followerBuilder.steps.intro.actions.goToNextStep"
        )}
      </Button>
    ),
    [STEPS.SKIN_SELECTION]: (
      <Button
        onClick={() => setActiveStep(STEPS.COLOR_SELECTION)}
        key="form-continue-button"
      >
        {t(
          "features.followers.followerBuilder.steps.skinSelection.actions.goToNextStep"
        )}
      </Button>
    ),
    [STEPS.COLOR_SELECTION]: (
      <div className="follower-builder__footer-buttons">
        <Button
          variant="secondary"
          block
          onClick={() => setActiveStep(STEPS.SKIN_SELECTION)}
          key="form-back-button"
        >
          {t(
            "features.followers.followerBuilder.steps.colorSelection.actions.goToPreviousStep"
          )}
        </Button>

        {doesSelectedSkinHaveMultipleVariations ? (
          <Button
            block
            onClick={() => setActiveStep(STEPS.VARIATION_SELECTION)}
            key="form-finish-button"
          >
            {t(
              "features.followers.followerBuilder.steps.colorSelection.actions.goToNextStep.variationsForSkin"
            )}
          </Button>
        ) : (
          <Button block onClick={handleClickFinish} key="form-finish-button">
            {t(
              "features.followers.followerBuilder.steps.colorSelection.actions.goToNextStep.noVariationsForSkin"
            )}
          </Button>
        )}
      </div>
    ),
    [STEPS.VARIATION_SELECTION]: (
      <div className="follower-builder__footer-buttons">
        <Button
          block
          variant="secondary"
          onClick={() => setActiveStep(STEPS.COLOR_SELECTION)}
          key="form-back-button"
        >
          {t(
            "features.followers.followerBuilder.steps.variationSelection.actions.goToPreviousStep"
          )}
        </Button>
        <Button block onClick={handleClickFinish} key="form-finish-button">
          {t(
            "features.followers.followerBuilder.steps.variationSelection.actions.goToNextStep"
          )}
        </Button>
      </div>
    ),
  };

  return (
    <Stack vertical spacing="default" className="follower-builder">
      <Stack vertical spacing="small" className="follower-builder__header">
        <p className="follower-builder__success-message">
          {t("features.followers.rafflePopup.subtitle")}
        </p>
        <EmbellishedHeading>
          {t("features.followers.followerBuilder.title")}
        </EmbellishedHeading>
      </Stack>

      {/*<DebugJSON value={selectedVariation} />*/}

      <div className="follower-builder__preview">
        <FollowerRenderer
          skinName={selectedVariation.variationSkinName}
          color={selectedVariation.color}
        />
      </div>

      <main className="follower-builder__content">
        {contentByStep[activeStep]}
      </main>

      <footer className="follower-builder__footer">
        {footerByStep[activeStep]}
      </footer>
    </Stack>
  );
};

export default FollowerBuilder;
